// When the camera is allowed to frame the board for you.
//
// One predicate, in a file of its own, because it has been wrong twice and
// both times the symptom was a camera pointing at nothing while every number
// in the rig was correct. It cannot be tested where it is used — the trigger
// lives in `BoardRenderer`, which needs a WebGL context — and it is pure, so
// it belongs out here with the other things `src/client/ai/render/` split out to make
// assertable (`armyTintOf`, `descentAt`, `CameraRig.covers`).

import type { Bounds } from "src/api";

/**
 * Should the camera reframe itself?
 *
 * **An opening frames itself; once you have played a move the camera is
 * yours.** The rig starts looking at the origin and a board is not required to
 * be there — a level's ground can be anywhere, and First Light's is centred on
 * (9, 9).
 *
 * Three cases, and the rule has to get all three right at once:
 *
 *  - **A new game.** The first board the renderer sees is the store's default
 *    classic eight-by-eight, because a level loads asynchronously and arrives a
 *    tick later. Framing only on the very first sync framed the placeholder and
 *    then never looked at the real board, so an extent *change* has to count.
 *  - **A resumed run.** `GameStore.restore` lands a snapshot carrying its real
 *    move history, so this opens at ply forty on a board several camps wide.
 *    The old test was "no move has been played", which is false on the very
 *    first frame — Continue opened at the default zoom over the origin with the
 *    board off screen somewhere.
 *  - **A stage boundary.** Ground opens twenty moves in, and the camera must
 *    *not* move: yanking the view off whatever you were reading is far worse
 *    than a badly framed first frame. This is why the rule is not simply
 *    "frame whenever the bounds change".
 *
 * What reconciles them is that "no move has been played" was only ever a proxy.
 * The real question is whether the player has taken the camera, and what taking
 * it looks like is a move played **since this renderer opened** — which is ply
 * zero for a new game and ply forty for a resumed run.
 */
/**
 * The rectangle a run should be framed on: the engine's `framed` — its whole
 * level, not the corner of it that has been revealed — capped so the board
 * keeps its share of the frame.
 */
/**
 * The least of the frame the board itself must keep.
 *
 * **Because the level's other regions are not drawn.** Fitting First Light's
 * whole level puts the playable lobe at 16% of the viewport — measured — and
 * the other 84% is not a map, it is nothing: unrevealed ground is not `cells`
 * yet, so there is literally nothing out there to see. Pulling back past the
 * point where the board stops being the thing you are looking at buys a sense
 * of scale at the price of the game.
 *
 * A half is the point where the board still reads as the subject and the space
 * around it says plainly that there is more level than this.
 */
const BOARD_SHARE = 0.5;

export function frameShare(board: Bounds, framed: Bounds): Bounds {
  const out = framed;
  if (
    out.minFile === board.minFile &&
    out.maxFile === board.maxFile &&
    out.minRank === board.minRank &&
    out.maxRank === board.maxRank
  )
    return board;

  // **The allowance is spent in the direction the level actually runs.**
  // Capping about the board's own centre is the obvious shape and it is wrong
  // whenever the level is off to one side: a level extending west would open
  // the frame *east*, over ground that is not there and away from the ground
  // that is. So each side is given a share of the allowance in proportion to
  // how far the level reaches that way, which for a level on one side spends
  // all of it on that side.
  const widen = (min: number, max: number, from: number, to: number) => {
    const span = to - from;
    const allowance = Math.max(0, span / BOARD_SHARE - span);
    const before = Math.max(0, from - min);
    const after = Math.max(0, max - to);
    const reach = before + after;
    if (reach <= allowance) return [min, max] as const;
    return [
      from - (allowance * before) / reach,
      to + (allowance * after) / reach
    ] as const;
  };
  const [minFile, maxFile] = widen(out.minFile, out.maxFile, board.minFile, board.maxFile);
  const [minRank, maxRank] = widen(out.minRank, out.maxRank, board.minRank, board.maxRank);
  return {
    minFile: Math.round(minFile),
    maxFile: Math.round(maxFile),
    minRank: Math.round(minRank),
    maxRank: Math.round(maxRank)
  };
}

export function shouldFrame(
  /** The board's extent now, as a comparable key. */
  extent: string,
  /** The extent the camera was last framed for; `""` before the first frame. */
  framed: string,
  /** How many plies have been played. */
  ply: number,
  /** How many had been played when this renderer first saw a board. */
  openedPly: number
): boolean {
  return extent !== framed && ply === openedPly;
}
